import React, { useMemo, useState, useRef, useEffect } from "react";
import { useFindMany } from "@gadgetinc/react";
import { XMarkIcon, PlusIcon } from "@heroicons/react/16/solid";

import { api } from "../../api";
import { filterOptions, getFilterOption } from "./filterOptions.jsx";
import { getFilterIcon, getFilterLabel } from "./filterDisplayUtils.js";
import FilterTypeSelector from "./FilterTypeSelector";
import FilterInput from "./FilterInput";
import ContactCount from "./ContactCount";
import ChatGPTFilterSearch from "./ChatGPTFilterSearch";

const tagToOption = ({ name, id }) => ({
  value: name.toLowerCase(),
  label: name,
  id,
});

/**
 * A compact chips-based filter bar supporting AND groups of OR conditions.
 * Supports:
 * - Phone number with multiple operators on `userPhoneNumber`
 * - Tag with multiple operators on `tags` (using contactTags relationship)
 *
 * groups shape: [{ ors: [{ type: 'phone'|'tag', operator: string, value: string, tagId?: string }] }]
 */
const ChipsContactFilter = ({
  groups,
  setGroups,
  customerId,
  customFields = [],
  onAddTag,
  recentCampaigns = [],
  activeAutomations = [],
  chatbots = [],
  integrations = {},
  onSaveSegmentClick,
  onContactCountChange,
}) => {
  const [editingFilter, setEditingFilter] = useState(null); // { groupIndex, orIndex, type, operator, value }
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [activeGroupIndex, setActiveGroupIndex] = useState(null);
  const [buttonPosition, setButtonPosition] = useState(null); // { x, y, width, height }
  const [containerWidth, setContainerWidth] = useState(800); // Default container width
  const [isGeneratingFilters, setIsGeneratingFilters] = useState(false);
  const containerRef = useRef(null);

  // Fetch available tags for autocomplete
  const [{ data: queryTags }] = useFindMany(api.tag, {
    first: 250,
    filter: [{ customerId: { equals: customerId } }],
    select: {
      id: true,
      name: true,
    },
  });

  const allTags = useMemo(() => {
    if (!queryTags) return [];
    return queryTags
      .map(tagToOption)
      .sort((tagA, tagB) => (tagA.label || "").localeCompare(tagB.label || ""));
  }, [queryTags]);

  const captureButtonPosition = (event) => {
    const button = event.currentTarget;
    const container = containerRef.current;
    if (button && container) {
      const buttonRect = button.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      setButtonPosition({
        x: buttonRect.left - containerRect.left,
        y: buttonRect.top - containerRect.top,
        width: buttonRect.width,
        height: buttonRect.height,
      });

      // Update container width
      setContainerWidth(containerRect.width);
    }
  };

  // Update container width on resize
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        setContainerWidth(containerRect.width);
      }
    };

    updateContainerWidth();
    window.addEventListener("resize", updateContainerWidth);

    return () => {
      window.removeEventListener("resize", updateContainerWidth);
    };
  }, []);

  const addAndGroup = (event) => {
    captureButtonPosition(event);
    setShowFilterDropdown(true);
    setActiveGroupIndex((groups || []).length);
  };

  const selectFilterType = (filterType) => {
    const filterOption = filterOptions.find((opt) => opt.type === filterType);
    if (!filterOption) return;

    const defaultOperator = filterOption.operators[0];
    setEditingFilter({
      groupIndex: activeGroupIndex,
      orIndex: null,
      type: filterType,
      operator: defaultOperator.key,
      operatorLabel: defaultOperator.label,
      requiresValue: defaultOperator.requiresValue,
      value: "",
    });
    setShowFilterDropdown(false);
  };

  const handleAddFilterClick = (event, groupIndex) => {
    captureButtonPosition(event);
    setActiveGroupIndex(groupIndex);
    setShowFilterDropdown(true);
  };

  const startEditingFilter = (event, groupIndex, orIndex) => {
    captureButtonPosition(event);
    const group = groups[groupIndex];
    const condition = group.ors[orIndex];

    const editingData = {
      groupIndex,
      orIndex,
      type: condition.type,
      operator: condition.operator,
      operatorLabel: condition.operatorLabel,
      requiresValue: condition.requiresValue,
      value: condition.value || "",
      // Pass custom field data if it's a custom field filter
      ...(condition.type === "customField" && {
        customFieldName: condition.customFieldName,
        customFieldId: condition.customFieldId,
      }),
      // Pass tag data if it's a tag filter
      ...(condition.type === "tag" && {
        tagId: condition.tagId,
      }),
      // Pass frequency data if it's a message filter
      ...((condition.type === "receivedMessageAt" ||
        condition.type === "placedOrderAt" ||
        condition.type === "openedMessageAt" ||
        condition.type === "clickedUrlAt" ||
        condition.type === "clickedButtonAt") && {
        frequency: condition.frequency,
        frequencyLabel: condition.frequencyLabel,
        campaignType: condition.campaignType,
        campaignTypeLabel: condition.campaignTypeLabel,
        campaignId: condition.campaignId,
      }),
    };

    setEditingFilter(editingData);
  };

  const saveFilter = (filterData) => {
    const updated = [...groups];
    const group = updated[filterData.groupIndex] || { ors: [] };

    if (filterData.orIndex !== null) {
      // Update existing filter
      group.ors[filterData.orIndex] = filterData;
    } else {
      // Add new filter
      group.ors = [...(group.ors || []), filterData];
    }

    updated[filterData.groupIndex] = group;
    setGroups(updated);
    setEditingFilter(null);
  };

  const cancelEditing = () => {
    setEditingFilter(null);
  };

  const removeOr = (groupIndex, orIndex) => {
    const updated = [...groups];
    const group = updated[groupIndex];
    if (group && group.ors) {
      group.ors.splice(orIndex, 1);
      if (group.ors.length === 0) {
        updated.splice(groupIndex, 1);
      }
      setGroups(updated);
    }
  };

  const handleFiltersGenerated = (generatedFilters) => {
    // Filter out empty groups to prevent showing empty filter buttons
    const validFilters = (generatedFilters || []).filter(
      (group) => group && group.ors && group.ors.length > 0,
    );
    setGroups(validFilters);
  };

  const handleLoadingChange = (loading) => {
    setIsGeneratingFilters(loading);
  };

  return (
    <>
      {/* Filter Type Dropdown */}
      <FilterTypeSelector
        isVisible={showFilterDropdown}
        onSelect={selectFilterType}
        onClose={() => setShowFilterDropdown(false)}
        buttonPosition={buttonPosition}
        containerWidth={containerWidth}
        customFields={customFields}
        integrations={integrations}
      />
      <div
        ref={containerRef}
        className="relative flex flex-wrap items-center gap-2 p-3 rounded-lg mt-2 mb-2"
      >
        {/* Filter Input */}
        {editingFilter && (
          <FilterInput
            filterType={editingFilter.type}
            initialOperator={filterOptions
              .find((opt) => opt.type === editingFilter.type)
              ?.operators.find((op) => op.key === editingFilter.operator)}
            initialValue={editingFilter.value}
            groupIndex={editingFilter.groupIndex}
            orIndex={editingFilter.orIndex}
            onSave={saveFilter}
            onCancel={cancelEditing}
            tagOptions={allTags}
            customFields={customFields}
            buttonPosition={buttonPosition}
            containerWidth={containerWidth}
            // Pass custom field data when editing
            initialCustomFieldName={editingFilter.customFieldName}
            // Pass tag data when editing
            initialTagId={editingFilter.tagId}
            // Pass frequency data when editing
            initialFrequency={editingFilter.frequency}
            // Pass campaign data when editing
            initialCampaignType={editingFilter.campaignType}
            initialCampaignId={editingFilter.campaignId}
          />
        )}

        {/* Render existing filter groups */}
        {(groups || []).map((group, gi) => (
          <React.Fragment key={`group-${gi}`}>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                {/* Single badge containing all OR conditions */}
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-s text-gray-800 border border-gray-200 shadow-sm">
                  {group.ors.map((cond, oi) => (
                    <React.Fragment key={`or-${gi}-${oi}`}>
                      <span className="group relative inline-flex items-center gap-1">
                        <button
                          onClick={(event) => startEditingFilter(event, gi, oi)}
                          className="text-gray-500 hover:text-gray-700"
                          type="button"
                          aria-label="Edit condition"
                        >
                          {getFilterIcon(cond.type)} {getFilterLabel(cond)}
                        </button>
                        <button
                          onClick={() => removeOr(gi, oi)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute top-1/2 -translate-y-1/2 -right-1 flex items-center justify-center w-5 h-5 rounded-full bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-700"
                          type="button"
                          aria-label="Remove condition"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                      {/* Show "or" between conditions in the same group, but not after the last one */}
                      {oi < group.ors.length - 1 && (
                        <div className="flex items-center">
                          <div className="w-px h-4 bg-transparent"></div>
                          <span className="text-gray-800 mx-1">or</span>
                          <div className="w-px h-4 bg-transparent"></div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                  {/* Vertical divider and "or +" button inside the badge */}
                  <div className="flex items-center ml-1">
                    <div className="w-px h-4 bg-transparent"></div>
                    <span className="text-gray-800 mx-1">or</span>
                    <div className="w-px h-4 bg-transparent"></div>
                    <button
                      className="ml-1 flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800 transition-colors duration-200"
                      onClick={(event) => handleAddFilterClick(event, gi)}
                      type="button"
                      aria-label="Add filter condition"
                    >
                      <PlusIcon className="h-3 w-3" />
                    </button>
                  </div>
                </span>
              </div>
            </div>

            {/* Show "and" between groups, but not after the last group */}
            {gi < (groups || []).length - 1 && (
              <span className="text-sm text-gray-800 mx-1">and</span>
            )}
          </React.Fragment>
        ))}

        {/* Single "+ Add filter" button and ChatGPT search */}
        <div className="w-full mt-2">
          {!groups || groups.length === 0 ? (
            // Show both "+ Add filter" and ChatGPT search on the same row when no filters
            <div className="flex items-center gap-3 w-full">
              <button
                className="text-sm text-pink flex-shrink-0 inline-flex items-center py-1 h-8"
                onClick={addAndGroup}
                type="button"
                disabled={isGeneratingFilters}
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add filter
              </button>
              <span className="text-sm text-gray-400 flex-shrink-0">or</span>
              <div className="flex-1 min-w-0 pt-3">
                <ChatGPTFilterSearch
                  onFiltersGenerated={handleFiltersGenerated}
                  customerId={customerId}
                  customFields={customFields}
                  tags={allTags}
                  isLoading={isGeneratingFilters}
                  onLoadingChange={handleLoadingChange}
                  recentCampaigns={recentCampaigns}
                  activeAutomations={activeAutomations}
                  chatbots={chatbots}
                  integrations={integrations}
                />
              </div>
            </div>
          ) : (
            // Show "+ Add filter" and "Save segment" when filters exist
            <div className="flex items-center gap-3 w-full">
              <button
                className="text-sm text-pink flex-shrink-0 inline-flex items-center py-1 h-8"
                onClick={addAndGroup}
                type="button"
                disabled={isGeneratingFilters}
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add filter
              </button>
              <button
                className="text-sm text-pink flex-shrink-0 inline-flex items-center py-1 h-8"
                type="button"
                onClick={onSaveSegmentClick}
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
                  />
                </svg>
                Save segment
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Contact Count Display */}
      <ContactCount
        filterGroups={groups}
        onAddTag={onAddTag}
        isGeneratingFilters={isGeneratingFilters}
        onCountChange={onContactCountChange}
      />
    </>
  );
};

export default ChipsContactFilter;
